/**
 * A form, as somebody sent a link to it sees it.
 *
 * Outside the shell and outside the session: a form exists to be filled in by
 * people who have no account here, so there is no sidebar, no workspace and no
 * sign-in.
 *
 * It is a column of the same editors the grid opens, which is the whole reason
 * a form is a small page rather than a second field surface. Two things differ
 * from a cell: every field is mounted at once, so none of them takes the focus
 * on mount; and the two editors that need more than a field are pointed at the
 * form's own public endpoints rather than at the table.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { fieldKey } from '@/api/types'
import { behaviorOf } from '@/features/database/fields/behavior'
import type { FieldContext } from '@/features/database/fields/behavior/behavior'
import { editorFor } from '@/features/database/fields/ui/edit'
import { EditorSourcesProvider } from '@/features/database/fields/ui/edit/sources'
import { visibleFormFields } from '@/features/database/form/conditions'
import {
  formFields,
  formQuery,
  submitForm,
  type FormField,
  type FormSubmitted,
} from '@/features/database/form/queries'
import {
  hiddenFrom,
  isHidden,
  needsLookup,
  prefillFor,
  prefillsFrom,
} from '@/features/database/form/prefill'
import { usePrefilledLinks } from '@/features/database/form/prefill-links'
import { formSources } from '@/features/database/form/sources'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/form/$slug'

export function FormPage() {
  const { slug } = useParams({ from: ROUTE })
  const search = useSearch({ from: ROUTE })
  const form = useQuery(formQuery(slug))

  if (form.isPending) return <FormSkeleton />
  if (form.data === undefined) return <FormNotFound />

  return (
    <Form
      key={slug}
      slug={slug}
      fields={formFields(form.data)}
      form={form.data}
      prefills={prefillsFrom(search)}
      hidden={hiddenFrom(search)}
    />
  )
}

function Form({
  slug,
  form,
  fields: asked,
  prefills,
  hidden,
}: {
  slug: string
  form: { title?: string; intro?: string; submit_text?: string }
  fields: readonly FormField[]
  prefills: ReadonlyMap<string, string>
  hidden: ReadonlySet<string>
}) {
  const { t } = useTranslation()

  // The link's answers *are* the drafts, for every question but one: a draft is
  // the text somebody typed, and a url parameter is text. So the initial state
  // is the link, and every editor reads it exactly as it reads what was typed.
  const [drafts, setDrafts] = useState<Record<number, string>>(() => {
    const started: Record<number, string> = {}
    for (const entry of asked) {
      if (needsLookup(entry.field)) continue
      const draft = prefillFor(entry, prefills)
      if (draft !== undefined) started[entry.field.id] = draft
    }
    return started
  })

  // The one that is not text: a link row's draft names rows by id and the link
  // names them by their text, so the rows have to be found before there is a
  // draft to hold. They arrive after the form is already on screen, which is
  // the honest version -- the alternative is holding the whole page back on a
  // lookup for one question.
  const links = usePrefilledLinks(slug, asked, prefills)
  useEffect(() => {
    // Behind what is already there, never over it: the lookup may land after
    // somebody has started answering, and a prefill is a starting point rather
    // than an instruction.
    setDrafts((held) => ({ ...links, ...held }))
  }, [links])
  const [missing, setMissing] = useState<readonly number[]>([])
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [done, setDone] = useState<FormSubmitted | null>(null)

  // Everybody a collaborator field on this form may name. The backend sends
  // the list on the field itself, which is the only way a visitor could have
  // it: they cannot read the workspace.
  const context: FieldContext = {
    collaborators: asked
      .flatMap(
        ({ field }) =>
          (field as { available_collaborators?: { id: number; name?: string }[] })
            .available_collaborators ?? [],
      )
      .map(({ id, name }) => ({ id, name: name ?? '' })),
  }

  // Which of the questions apply, given what has been answered so far.
  // Recomputed on every keystroke rather than watched: a condition may read any
  // earlier answer, so there is nothing narrower to depend on, and the whole of
  // the rule is a pure function over the drafts.
  //
  // Everything below reads *this* list. A question that is not being asked is
  // not validated as required and its answer is not sent -- which is also why
  // the backend cannot check `required` for a conditional question and says so
  // in `FormViewFieldOptions.is_required`.
  const fields = visibleFormFields(asked, drafts, context)

  /**
   * The ones actually on the page.
   *
   * A question the link hides is still *answered* -- with whatever the link
   * prefilled it with -- and still read by any condition that names it. Hiding
   * and prefilling together is the whole point of being able to do both: a link
   * sent to one supplier names the supplier and does not ask.
   *
   * It is not checked for being required, which is the one place upstream gets
   * this wrong: it hides the field with a CSS class and then refuses to submit
   * the form, pointing at a question nobody can see, let alone answer.
   */
  const shown = fields.filter((entry) => !isHidden(entry, hidden))

  /** What each field would send, which is also what says whether it is filled in. */
  const valueOf = (field: Field): unknown => {
    const editor = editorFor(field)
    const draft = drafts[field.id]
    if (editor === null || draft === undefined) return undefined
    return editor.fromDraft(field, draft, context)
  }

  function onSubmit(event: React.FormEvent): void {
    event.preventDefault()

    // Checked here as well as on the server, because a round trip to be told
    // a box is empty is a round trip somebody waits for. `isEmpty` is the
    // field type's own answer, so a rating of zero and an unticked box count
    // as empty exactly where the backend counts them so.
    const empty = shown
      .filter(({ required, field }) => {
        if (required !== true) return false
        const value = valueOf(field)
        return value === undefined || behaviorOf(field.type).isEmpty(field, value)
      })
      .map(({ field }) => field.id)

    setMissing(empty)
    if (empty.length > 0) return

    const values: Record<string, unknown> = {}
    for (const { field } of fields) {
      const value = valueOf(field)
      if (value !== undefined) values[fieldKey(field.id)] = value
    }

    setSending(true)
    setFailed(false)
    void submitForm(slug, values)
      .then((answer) => {
        // Only ever to a page. A form's owner writes this url, and a visitor
        // is being sent to it -- so a `javascript:` one would be somebody
        // else's script running on the click of a submit button.
        // `{row_id}` is substituted here rather than by the backend, which
        // never sees the url: the redirect is the browser's, and the id of the
        // row that was just created is what a form's owner most often wants to
        // carry into wherever they are sending people next.
        const url = answer.submit_action_redirect_url?.replaceAll(
          '{row_id}',
          String(answer.row_id),
        )
        if (answer.submit_action === 'REDIRECT' && url !== undefined && isSafeUrl(url)) {
          window.location.assign(url)
          return
        }
        setDone(answer)
      })
      .catch(() => {
        setFailed(true)
      })
      .finally(() => {
        setSending(false)
      })
  }

  if (done !== null) {
    return (
      <Shell>
        <Empty className="p-6">
          <EmptyHeader>
            <EmptyTitle>
              {done.submit_action_message !== undefined &&
              done.submit_action_message !== ''
                ? done.submit_action_message
                : t('form.thanks')}
            </EmptyTitle>
          </EmptyHeader>
        </Empty>
      </Shell>
    )
  }

  return (
    <Shell>
      <EditorSourcesProvider sources={formSources(slug)}>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6">
          <header className="flex flex-col gap-2">
            {form.title !== undefined && form.title !== '' && (
              <h1 className="text-2xl font-semibold">{form.title}</h1>
            )}
            {form.intro !== undefined && form.intro !== '' && (
              <p className="text-muted-foreground">{form.intro}</p>
            )}
          </header>

          {shown.map((entry) => (
            <FormRow
              key={entry.field.id}
              entry={entry}
              context={context}
              draft={drafts[entry.field.id] ?? ''}
              missing={missing.includes(entry.field.id)}
              onDraft={(draft) => {
                setDrafts((held) => ({ ...held, [entry.field.id]: draft }))
              }}
            />
          ))}

          {failed && <p className="text-sm text-destructive">{t('form.failed')}</p>}

          <Button type="submit" disabled={sending} className="self-start">
            {form.submit_text !== undefined && form.submit_text !== ''
              ? form.submit_text
              : t('form.submit')}
          </Button>
        </form>
      </EditorSourcesProvider>
    </Shell>
  )
}

function FormRow({
  entry,
  context,
  draft,
  missing,
  onDraft,
}: {
  entry: FormField
  context: FieldContext
  draft: string
  missing: boolean
  onDraft: (draft: string) => void
}) {
  const { t } = useTranslation()
  const editor = editorFor(entry.field)
  const label = entry.name === '' ? entry.field.name : entry.name

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium" htmlFor={`field-${entry.field.id}`}>
        {label}
        {entry.required === true && (
          <span aria-hidden className="ml-1 text-destructive">
            *
          </span>
        )}
      </label>
      {entry.description !== undefined && entry.description !== '' && (
        <p className="text-sm text-muted-foreground">{entry.description}</p>
      )}

      {editor === null ? (
        // A field the form asks for that this application cannot draw. Said
        // out loud rather than left blank: a required one makes the form
        // unsubmittable, and somebody staring at a gap deserves to know why.
        <p className="text-sm text-muted-foreground">{t('form.unsupported')}</p>
      ) : (
        <editor.Component
          field={entry.field}
          context={context}
          value={draft}
          label={label}
          // Every field is mounted at once, so none of them takes the focus.
          autoFocus={false}
          onValueChange={onDraft}
          onKeyDown={() => {
            // The grid uses this to commit and move on. A form has neither.
          }}
        />
      )}

      {missing && <p className="text-sm text-destructive">{t('form.required')}</p>}
    </div>
  )
}

/** Whether a url is one a browser should be sent to. */
function isSafeUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function Shell({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto min-h-svh w-full max-w-2xl px-4 py-10">{children}</main>
}

function FormSkeleton() {
  return (
    <Shell>
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </Shell>
  )
}

function FormNotFound() {
  const { t } = useTranslation()
  return (
    <Shell>
      <Empty className="p-6">
        <EmptyHeader>
          <EmptyTitle>{t('form.notFoundTitle')}</EmptyTitle>
          <EmptyDescription>{t('form.notFoundDescription')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    </Shell>
  )
}
